import './footer.sass';
import { Container, Row, Col, Image } from 'react-bootstrap';
import { Link, NavLink, useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { Link as ScrollLink } from 'react-scroll';

// Import Images
import android_logo from '../../assets/images/android.png';
import ios_logo from '../../assets/images/ios.png';
import nobel_logo from '../../assets/images/nobel_logo.png';
import facebook_logo from '../../assets/images/facebook.png';
import instagram_logo from '../../assets/images/instagram.png';
import Linkedin_logo from '../../assets/images/linkedin.png';

const nobel_url = '/';

interface Section {
  title: React.ReactNode;
  links?: { text: React.ReactNode; url?: string; sectionId?: string }[]; 
  logos?: { href: string; src: string; alt: string }[];
}

function Footer() {
  const { t } = useTranslation(); // Declare t once here
  const navigate = useNavigate(); // Use navigate here

  const navigateLinks = (link: { url?: string; sectionId?: string }) => {
    if (link.url) {
      navigate(`/${link.url}`);
    } else if (link.sectionId) {
      navigate(nobel_url); // Navigate to the homepage first
      setTimeout(() => {
        const scrollTarget = document.getElementById(link.sectionId || '');
        if (scrollTarget) {
          scrollTarget.scrollIntoView({
            behavior: 'smooth',
          });
        }
      }, 300); // Give time for page navigation to complete
    }
  };

  return (
    <div className="footer-container">
      <Container>
        <FooterLeft navigateLinks={navigateLinks} />
        <FooterBottom t={t} /> {/* Pass t to FooterBottom */}
      </Container>
    </div>
  );
}

function FooterLeft({ navigateLinks }: { navigateLinks: any }) {
  const { t } = useTranslation(); // Declare t here for translation

  const sections: Section[] = [
    {
      title: <Trans i18nKey="product" />,
      links: [
        { text: t('about_title'), url: 'about' },
        { text: t('contact_title'), sectionId: 'contact' },
        { text: t('delete_account'), url: 'delete-account' },
      ],
    },
    {
      title: <Trans i18nKey="customer_app" />,
      logos: [
        { href: 'https://play.google.com/', src: android_logo, alt: 'android_logo' },
        { href: 'https://www.apple.com/pt/app-store/', src: ios_logo, alt: 'ios_logo' },
      ],
    },
    {
      title: <Trans i18nKey="provider_app" />,
      logos: [
        { href: 'https://play.google.com/', src: android_logo, alt: 'android_logo' },
        { href: 'https://www.apple.com/pt/app-store/', src: ios_logo, alt: 'ios_logo' },
      ],
    },
  ];

  return (
    <Row className="footer-content">
      <Col xs={12} md={7} className="footer-left">
        {/* Logo & Name */}
        <div className="div-nobel">
          <Link to="/" className="nobel-link">
            <Image src={nobel_logo} className="nobel-logo-footer" alt="nobel-logo" fluid />
            <h4 className="h4-nobel">Nobel</h4>
          </Link>
        </div>

        {/* Home Services Text */}
        <div className="home-services">
          <div>
            <Trans i18nKey="home_services.line1" />
          </div>
          <div>
            <Trans i18nKey="home_services.line2" />
          </div>
        </div>

        {/* Social Media Icons */}
        <Row className="social-icons">
          {[{
            href: 'https://www.facebook.com',
            src: facebook_logo,
            alt: 'facebook_logo',
          }, {
            href: 'https://www.instagram.com',
            src: instagram_logo,
            alt: 'instagram_logo',
          }, {
            href: 'https://www.linkedin.com',
            src: Linkedin_logo,
            alt: 'linkedin_logo',
          }].map((logo, index) => (
            <Col xs="auto" key={index} className="social-logo-col">
              <a href={logo.href} target="_blank" rel="noopener noreferrer">
                <Image src={logo.src} alt={logo.alt} className="social-logo" roundedCircle />
              </a>
            </Col>
          ))}
        </Row>
      </Col>

      {/* Links & Apps */}
      <Col xs={12} md={5}>
        <Row className="footer-links">
          {sections.map((section, index) => (
            <FooterRight key={index} section={section} navigateLinks={navigateLinks} />
          ))}
        </Row>
      </Col>
    </Row>
  );
}

function FooterRight({ section, navigateLinks }: { section: Section; navigateLinks: any }) {
  return (
    <Col xs={12} sm={6} md={4} className="footer-right">
      <h4 className="footer-title">{section.title}</h4>
      {section.links &&
        section.links.map((link, index) => (
          <p key={index}>
            {link.url ? (
              // If the link has a URL, use NavLink for navigation
              <NavLink
                to={`/${link.url}`}
                className={({ isActive }) => isActive ? 'footer-link active' : 'footer-link'}
                onClick={() => navigateLinks(link)} // Handle click for routing
              >
                {link.text}
              </NavLink>
            ) : (
              // If the link has a sectionId, use ScrollLink for smooth scrolling
              <ScrollLink
                to={link.sectionId || ''}
                spy={true}
                offset={-100}
                smooth={true}
                duration={500}
                className="footer-link"
                activeClass="footer-link"
                onClick={() => navigateLinks(link)} // Handle click for scrolling
              >
                {link.text}
              </ScrollLink>
            )}
          </p>
        ))}
      {section.logos &&
        section.logos.map((logo) => (
          <a href={logo.href} key={logo.href} target="_blank" rel="noopener noreferrer">
            <Image src={logo.src} className="app-logo" alt={logo.alt} />
          </a>
        ))}
    </Col>
  );
}

function FooterBottom({ t }: { t: any }) {
  return (
    <Row className="footer-bottom">
      <Col xs={12} md={6} className="rights-reserved">
        © {new Date().getFullYear()} {t('rights_reserved')}
      </Col>
      <Col xs={12} md={6} className="terms-conditions">
        <Link to="/privacy">{t('privacy_intro_title')}</Link> ·{' '}
        <Link to="/terms">{t('termsAndConditions_title')}</Link>
      </Col>
    </Row>
  );
}

export { Footer };
